import React, { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router";
import { publicRoutes } from "./router.link";
import PageLoader from "../../core/common/pageLoader/PageLoader";
import Feature from "../feature";
import ProtectedRoute from "../../protected/ProtectedRoute";
import RoleProtectedRoute from "../../protected/RoleProtectedRoute";
import PracticeProtectedRoute from "../../protected/PracticeProtectedRoute";
import PracticeApprovedRoute from "../../protected/PracticeApprovedRoute";
import SpeakingPartnerLayout from "../speaking-partner/SpeakingPartnerLayout";

// Pages load on demand (own JS chunk), so visitors don't download the whole app first
const Checkout = lazy(() => import("../Courses/course-checkout/Checkout"));
const StudentDashboard = lazy(() => import("../student/StudentDashboard"));
const PracticeAuthPage = lazy(() => import("../speaking-partner/auth/PracticeAuthPage"));
const PracticeForgotPasswordPage = lazy(() => import("../speaking-partner/auth/PracticeForgotPasswordPage"));
const PracticeResetPasswordPage = lazy(() => import("../speaking-partner/auth/PracticeResetPasswordPage"));
const PracticeDashboard = lazy(() => import("../speaking-partner/PracticeDashboard"));
const PracticePendingApproval = lazy(() => import("../speaking-partner/PracticePendingApproval"));
const PracticeProfilePage = lazy(() => import("../speaking-partner/PracticeProfilePage"));
const PracticeBrowsePage = lazy(() => import("../speaking-partner/PracticeBrowsePage"));
const PracticeBlockedPage = lazy(() => import("../speaking-partner/PracticeBlockedPage"));
const PracticeInboxPage = lazy(() => import("../speaking-partner/PracticeInboxPage"));
const PracticeChatPage = lazy(() => import("../speaking-partner/PracticeChatPage"));
const PracticeVoiceCall = lazy(() => import("../speaking-partner/PracticeVoiceCall"));
const PracticeGroupCallPage = lazy(() => import("../speaking-partner/PracticeGroupCallPage"));
const PracticeHowItWorksPage = lazy(() => import("../speaking-partner/PracticeHowItWorksPage"));
const AdminDashboard = lazy(() => import("../admin/AdminDashboard"));
const MentorDashboard = lazy(() => import("../mentor/MentorDashboard"));
const EmployeeDashboard = lazy(() => import("../employee/EmployeeDashboard"));
const EmployeeDailyActivity = lazy(() => import("../employee/EmployeeDailyActivity"));
const ManageStudents = lazy(() => import("../admin/ManageStudents"));
const EditStudent = lazy(() => import("../admin/EditStudent"));
const EditCourse = lazy(() => import("../admin/EditCourse"));
const CreateCourse = lazy(() => import("../admin/CreateCourse"));
const ManageCourses = lazy(() => import("../admin/ManageCourses"));
const ManageBatches = lazy(() => import("../admin/ManageBatches"));
const AdminCoursePlans = lazy(() => import("../admin/AdminCoursePlans"));
const CreateBatch = lazy(() => import("../admin/CreateBatch"));
const AdminEnrollments = lazy(() => import("../admin/AdminEnrollments"));
const StudentCourses = lazy(() => import("../student/StudentCourses"));
const ManageAppointments = lazy(() => import("../admin/ManageAppointments"));
const EditBatch = lazy(() => import("../admin/EditBatch"));
const MentorBatches = lazy(() => import("../mentor/MentorBatches"));
const AdminManageQuizzes = lazy(() => import("../admin/AdminManageQuizzes"));
const CreateQuizPage = lazy(() => import("../quiz/CreateQuizPage"));
const AddQuestionsPage = lazy(() => import("../quiz/AddQuestionsPage"));
const EditQuizPage = lazy(() => import("../admin/EditQuizPage"));
const AssignQuizPage = lazy(() => import("../quiz/AssignQuizPage"));
const StudentBatch = lazy(() => import("../student/StudentBatch"));
const AdminBatchQuizSummary = lazy(() => import("../quiz/AdminBatchQuizSummary"));
const MentorBatchQuizOverview = lazy(() => import("../mentor/MentorManageQuiz"));
const StudentAttendanceOverview = lazy(() => import("../student/StudentAttendanceOverview"));
const ChatApp = lazy(() => import("../student/ChatApp"));
const MentorBatchCourseProgress = lazy(() => import("../mentor/MentorBatchCourseProgress"));
const AdminPaymentManagement = lazy(() => import("../admin/AdminPaymentManagement"));
const DueInstallments = lazy(() => import("../admin/DueInstallments"));
const RequireActiveStudent = lazy(() => import("../student/RequireActiveStudent"));
const ManageAdminsMentors = lazy(() => import("../admin/ManageAdminsMentors"));
const FinancialDashboard = lazy(() => import("../admin/FinancialDashboard"));
const AdminStepManagement = lazy(() => import("../admin/AdminStepManagement"));
const StudentProgressDashboard = lazy(() => import("../student/StudentProgressDashboard"));
const MentorProgressDashboard = lazy(() => import("../mentor/MentorProgressDashboard"));
const AdminProgressDashboard = lazy(() => import("../admin/AdminProgressDashboard"));
const MentorRegisterPage = lazy(() => import("../admin/MentorRegisterPage"));
const ForgotPasswordPage = lazy(() => import("../auth/login/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("../auth/login/ResetPasswordPage"));
const LegacyRegister = lazy(() => import("../auth/register/LegacyRegister"));
const AdminLegacyPending = lazy(() => import("../admin/AdminLegacyPending"));
const AdminBroadcastEmail = lazy(() => import("../admin/AdminBroadcastEmail"));
const TranslationRequests = lazy(() => import("../student/TranslationRequests"));
const TranslationRequestsAdmin = lazy(() => import("../admin/TranslationRequestsAdmin"));
const TranslationQueue = lazy(() => import("../employee/TranslationQueue"));
const CustomerLayout = lazy(() => import("../customer/CustomerLayout"));
const CustomerOverview = lazy(() => import("../customer/CustomerOverview"));
const CustomerOrders = lazy(() => import("../customer/CustomerOrders"));
const CustomerNewOrder = lazy(() => import("../customer/CustomerNewOrder"));
const CustomerProfile = lazy(() => import("../customer/CustomerProfile"));
const RegistrationSuccess = lazy(() => import("../auth/register/RegistrationSuccess"));
const SchreibenPracticePage = lazy(() => import("../student/SchreibenPracticePage"));
const AdminWritingTasksPage = lazy(() => import("../admin/AdminWritingTasksPage"));
const CreateWritingTaskPage = lazy(() => import("../admin/CreateWritingTaskPage"));
const EditWritingTaskPage = lazy(() => import("../admin/EditWritingTaskPage"));
const AssignWritingTaskPage = lazy(() => import("../admin/AssignWritingTaskPage"));
const MentorWritingTasksPage = lazy(() => import("../mentor/MentorWritingTasksPage"));
const MentorAssignWritingTaskPage = lazy(() => import("../mentor/MentorAssignWritingTaskPage"));
const StudentMyWritingTasksPage = lazy(() => import("../student/StudentMyWritingTasksPage"));
const StudentWritingTaskPage = lazy(() => import("../student/StudentWritingTaskPage"));
const StudentQuiz = lazy(() => import("../student/StudentQuiz"));
const MentorBatchWritingTaskResultsPage = lazy(() => import("../mentor/MentorBatchWritingTaskResultsPage"));
const LoginPage = lazy(() => import("../auth/login/LoginPage"));
const EnrollmentConfirmation = lazy(() => import("../Courses/course-checkout/EnrollmentConfirmation"));
const AdminAttendanceOverview = lazy(() => import("../admin/AdminAttendanceOverview"));
const CourseRegulations = lazy(() => import("../student/CourseRegulations"));
const AdminRegulationReview = lazy(() => import("../admin/AdminRegulationReview"));
const CourseRegulationTemplates = lazy(() => import("../admin/CourseRegulationTemplates"));
const AusbildungApplicationTracking = lazy(() => import("../student/AusbildungApplicationTracking"));
const AdminAusbildungDashboard = lazy(() => import("../admin/applicationtracker/AdminAusbildungDashboard"));
const VolunteerCircularPage = lazy(() => import("../volunteer/VolunteerCircularPage"));
const AdminVolunteerAccess = lazy(() => import("../admin/AdminVolunteerAccess"));
const AdminVisaLetterCheck = lazy(() => import("../admin/AdminVisaLetterCheck"));
const AdminVisaWaitingCases = lazy(() => import("../admin/AdminVisaWaitingCases"));
const AdminHomeworkPage = lazy(() => import("../admin/homework/AdminHomeworkPage"));
const AdminCreateHomeworkPage = lazy(() => import("../admin/homework/AdminCreateHomeworkPage"));
const AdminEditHomeworkPage = lazy(() => import("../admin/homework/AdminEditHomeworkPage"));
const AdminAssignHomeworkPage = lazy(() => import("../admin/homework/AdminAssignHomeworkPage"));
const MentorHomeworkPage = lazy(() => import("../mentor/homework/MentorHomeworkPage"));
const MentorHomeworkViewPage = lazy(() => import("../mentor/homework/MentorHomeworkViewPage"));
const MentorAssignHomeworkPage = lazy(() => import("../mentor/homework/MentorAssignHomeworkPage"));
const StudentMyHomeworkPage = lazy(() => import("../student/homework/StudentMyHomeworkPage"));
const StudentHomeworkCompletePage = lazy(() => import("../student/homework/StudentHomeworkCompletePage"));
const StudentHomeworkSubmissionPage = lazy(() => import("../student/homework/StudentHomeworkSubmissionPage"));
const MentorManageQuiz = lazy(() => import("../mentor/MentorManageQuiz"));
const StudentVocabulary = lazy(() => import("../student/StudentVocabulary"));
const AdminVocabularyManagement = lazy(() => import("../admin/vocab/Adminvocabularymanagement"));
const AdminHomeworkViewPage = lazy(() => import("../admin/homework/AdminHomeworkViewPage"));
const AdminExamManagement = lazy(() => import("../admin/exam/AdminExamManagement"));
const AdminCreateExamSet = lazy(() => import("../admin/exam/AdminCreateExamSet"));
const AdminEditExamSet = lazy(() => import("../admin/exam/AdminEditExamSet"));
const AdminManageQuestions = lazy(() => import("../admin/exam/AdminManageQuestions"));
const AdminAssignExam = lazy(() => import("../admin/exam/AdminAssignExam"));
const AdminExamResults = lazy(() => import("../admin/exam/AdminExamResults"));
const AdminExamStatistics = lazy(() => import("../admin/exam/AdminExamStatistics"));
const AdminExamAssignments = lazy(() => import("../admin/exam/AdminExamAssignments"));
const AdminExamBuilder = lazy(() => import("../admin/exam/AdminExamBuilder"));
const MentorExamManagement = lazy(() => import("../mentor/exam/MentorExamManagement"));
const MentorGradeExam = lazy(() => import("../mentor/exam/MentorGradeExam"));
const MentorExamResults = lazy(() => import("../mentor/exam/MentorExamResults"));
const StudentMyExams = lazy(() => import("../student/exam/StudentMyExams"));
const StudentTakeExam = lazy(() => import("../student/exam/StudentTakeExam"));
const StudentExamResult = lazy(() => import("../student/exam/StudentExamResult"));
const AdminBatchGrading = lazy(() => import("../admin/exam/AdminBatchGrading"));
const AdminClassTestManagement = lazy(() => import("../admin/classtest/AdminClassTestManagement"));
const MentorClassTestManagement = lazy(() => import("../mentor/classtest/MentorClassTestManagement"));
const StudentClassTestPage = lazy(() => import("../student/classtest/StudentClassTestPage"));
const CoverLetterAccessManagement = lazy(() => import("../admin/CoverLetterAccessManagement"));
const CvBuilderAccessManagement = lazy(() => import("../admin/CvBuilderAccessManagement"));
const CoverLetterGenerator = lazy(() => import("../student/CoverLetterGenerator"));
const StudentCVBuilder = lazy(() => import("../student/StudentCVBuilder"));
const GradeConverter = lazy(() => import("../student/GradeConverter"));
const CVBuilderGuide = lazy(() => import("../student/CVBuilderGuide"));
const Blog = lazy(() => import("../blog/Blog"));
const BlogDetails = lazy(() => import("../blog/BlogDetails"));
const AdminBlogManagement = lazy(() => import("../admin/blog/AdminBlogManagement"));
const CreateEditBlog = lazy(() => import("../admin/blog/CreateEditBlog"));
const AdminCalendarManagement = lazy(() => import("../admin/AdminCalendarManagement"));
const AdminShadowingTasksPage = lazy(() => import("../admin/shadowing/AdminShadowingTasksPage"));
const CreateShadowingTaskPage = lazy(() => import("../admin/shadowing/CreateShadowingTaskPage"));
const EditShadowingTaskPage = lazy(() => import("../admin/shadowing/EditShadowingTaskPage"));
const AssignShadowingTaskPage = lazy(() => import("../admin/shadowing/AssignShadowingTaskPage"));
const StudentShadowingTaskPage = lazy(() => import("../student/shadowing/StudentShadowingTaskPage"));
const StudentMyShadowingTasksPage = lazy(() => import("../student/shadowing/StudentMyShadowingTasksPage"));
const ReviewShadowingSubmissionPage = lazy(() => import("../admin/shadowing/ReviewShadowingSubmissionPage"));
const MentorShadowingTasksPage = lazy(() => import("../mentor/shadowing/MentorShadowingTasksPage"));
const MentorAssignShadowingTaskPage = lazy(() => import("../mentor/shadowing/MentorAssignShadowingTaskPage"));
const MentorBatchShadowingTaskSummary = lazy(() => import("../mentor/shadowing/MentorBatchShadowingTaskSummary"));
const MentorReviewShadowingSubmissionPage = lazy(() => import("../mentor/shadowing/MentorReviewShadowingSubmissionPage"));
const AdminShadowingSubmissionsListPage = lazy(() => import("../admin/shadowing/AdminShadowingSubmissionsListPage"));
const MentorReviewShadowingSubmissionDetailPage = lazy(() => import("../mentor/shadowing/MentorReviewShadowingSubmissionDetailPage"));
const MonthlyVisits = lazy(() => import("../admin/MonthlyVisits"));
const AdminActivityReport = lazy(() => import("../admin/AdminActivityReport"));
const CourseLecturesLayout = lazy(() => import("../admin/courseLectures/CourseLecturesLayout"));
const StudentAudioPage = lazy(() => import("../student/audio/StudentAudioPage"));
const AdminAudioReviewPage = lazy(() => import("../admin/audio/AdminAudioReviewPage"));
const MentorAudioReviewPage = lazy(() => import("../mentor/audio/MentorAudioReviewPage"));
const AdminMentorTracker = lazy(() => import("../admin/AdminMentorTracker"));
const AdminEmployeeActivity = lazy(() => import("../admin/AdminEmployeeActivity"));
const AdminPracticeManagementPage = lazy(() => import("../admin/practice/AdminPracticeManagementPage"));
const MentorTaskCheckinPage = lazy(() => import("../mentor/MentorTaskCheckinPage"));


// ✅ BLOG IMPORTS

const ALLRoutes: React.FC = () => {
  return (
    // Pages outside the layouts (login, dashboards) show the loader on their own
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* CRITICAL: Auth routes MUST come first before Feature wrapper */}
        {/* <Route path="/register" element={<RegisterPage />} /> */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* ✅ ADD ENROLLMENT CONFIRMATION ROUTE HERE - Must be protected */}
        <Route element={<ProtectedRoute />}>
          <Route
            path="/enrollment-confirmation"
            element={<EnrollmentConfirmation />}
          />
        </Route>

        {/* Legacy Registration - Also outside Feature */}
        <Route path="/legacy-register" element={<LegacyRegister />} />
        <Route path="/registration-success" element={<RegistrationSuccess />} />

        {/* Speaking Partner - Live — fully independent auth, not the site's Student/Mentor/Admin login */}
        <Route path="/speaking-partner" element={<SpeakingPartnerLayout />}>
          <Route index element={<PracticeDashboard />} />
          <Route path="login" element={<PracticeAuthPage />} />
          <Route path="register" element={<PracticeAuthPage />} />
          <Route path="forgot-password" element={<PracticeForgotPasswordPage />} />
          <Route path="reset-password" element={<PracticeResetPasswordPage />} />
          <Route path="how-it-works" element={<PracticeHowItWorksPage />} />
          <Route element={<PracticeProtectedRoute />}>
            <Route path="pending" element={<PracticePendingApproval />} />
            <Route path="profile" element={<PracticeProfilePage />} />
            <Route element={<PracticeApprovedRoute />}>
              <Route path="browse" element={<PracticeBrowsePage />} />
              <Route path="blocked" element={<PracticeBlockedPage />} />
              <Route path="chats" element={<PracticeInboxPage />} />
              <Route path="chats/:chatId" element={<PracticeChatPage />} />
              <Route path="chats/:chatId/call" element={<PracticeVoiceCall />} />
              <Route path="group-calls/:hostChatId/call" element={<PracticeGroupCallPage />} />
            </Route>
          </Route>
        </Route>

        {/* Public Pages wrapped in Feature */}
        <Route element={<Feature />}>
          {publicRoutes
            .filter(
              (route) =>
                route.path !== "/register" &&
                route.path !== "/login" &&
                route.path !== "/forgot-password" &&
                route.path !== "/reset-password",
            )
            .map((route, idx) => (
              <Route path={route.path} element={route.element} key={idx} />
            ))}

          {/* ✅ BLOG PUBLIC ROUTES - Add these inside Feature wrapper */}
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:id" element={<BlogDetails />} />
        </Route>

        {/* General Protected Pages (any authenticated user) */}
        <Route element={<ProtectedRoute />}>
          <Route path="/course/checkout" element={<Checkout />} />
        </Route>

        {/* PUBLIC TRANSLATION CUSTOMERS (role CUSTOMER) — inside the site layout, with their own sidebar */}
        <Route element={<RoleProtectedRoute allowedRoles={["CUSTOMER"]} />}>
          <Route element={<Feature />}>
            <Route path="/account" element={<CustomerLayout />}>
              <Route index element={<CustomerOverview />} />
              <Route path="orders" element={<CustomerOrders />} />
              <Route path="new-order" element={<CustomerNewOrder />} />
              <Route path="profile" element={<CustomerProfile />} />
            </Route>
          </Route>
        </Route>

        {/* STUDENT ROLE PROTECTED ROUTES */}
        <Route element={<RoleProtectedRoute allowedRoles={["STUDENT"]} />}>
          {/* Always accessible to students (active or inactive) */}
          <Route
            path="/student/student-dashboard"
            element={<StudentDashboard />}
          />
          <Route path="/student/my-courses" element={<StudentCourses />} />
          <Route
            path="/student/course-progress"
            element={<StudentProgressDashboard />}
          />
          <Route
            path="/student/course-regulations"
            element={<CourseRegulations />}
          />
          <Route
            path="/student/translation-requests"
            element={<TranslationRequests />}
          />
          {/* ACTIVE STUDENT ONLY */}
          <Route
            path="/student/my-batch"
            element={
              <RequireActiveStudent>
                <StudentBatch />
              </RequireActiveStudent>
            }
          />
          <Route
            path="/student/my-attendance"
            element={
              <RequireActiveStudent>
                <StudentAttendanceOverview />
              </RequireActiveStudent>
            }
          />
          <Route
            path="/student/study-assistant"
            element={
              <RequireActiveStudent>
                <ChatApp />
              </RequireActiveStudent>
            }
          />
          <Route
            path="/student/writing-practice"
            element={
              <RequireActiveStudent>
                <React.Suspense fallback={<div>Loading...</div>}>
                  <SchreibenPracticePage />
                </React.Suspense>
              </RequireActiveStudent>
            }
          />
          <Route
            path="/student/my-writing-tasks"
            element={
              <RequireActiveStudent>
                <React.Suspense fallback={<div>Loading...</div>}>
                  <StudentMyWritingTasksPage />
                </React.Suspense>
              </RequireActiveStudent>
            }
          />
          <Route
            path="/student/writing-task/:taskId"
            element={
              <RequireActiveStudent>
                <React.Suspense fallback={<div>Loading...</div>}>
                  <StudentWritingTaskPage />
                </React.Suspense>
              </RequireActiveStudent>
            }
          />
          <Route
            path="/student/manage-quizzes"
            element={
              <RequireActiveStudent>
                <StudentQuiz />
              </RequireActiveStudent>
            }
          />
          <Route
            path="/student/ausbildung-application-tracking"
            element={
              <RequireActiveStudent>
                <AusbildungApplicationTracking />
              </RequireActiveStudent>
            }
          />
          <Route
            path="/student/my-homework"
            element={
              <RequireActiveStudent>
                <StudentMyHomeworkPage />
              </RequireActiveStudent>
            }
          />
          <Route
            path="/student/homework/:homeworkId/complete"
            element={
              <RequireActiveStudent>
                <StudentHomeworkCompletePage />
              </RequireActiveStudent>
            }
          />
          <Route
            path="/student/homework/:homeworkId/submission"
            element={
              <RequireActiveStudent>
                <StudentHomeworkSubmissionPage />
              </RequireActiveStudent>
            }
          />

          <Route
            path="/student/vocabulary"
            element={
              <RequireActiveStudent>
                <StudentVocabulary />
              </RequireActiveStudent>
            }
          />

          <Route
            path="/student/my-exams"
            element={
              <RequireActiveStudent>
                <StudentMyExams />
              </RequireActiveStudent>
            }
          />

          <Route
            path="/student/exam/:assignmentId/take"
            element={
              <RequireActiveStudent>
                <StudentTakeExam />
              </RequireActiveStudent>
            }
          />

          <Route
            path="/student/exam/:attemptId/result"
            element={
              <RequireActiveStudent>
                <StudentExamResult />
              </RequireActiveStudent>
            }
          />

          <Route
            path="/student/my-class-tests"
            element={
              <RequireActiveStudent>
                <StudentClassTestPage />
              </RequireActiveStudent>
            }
          />

          <Route
            path="/student/cover-letter-generator"
            element={
              <RequireActiveStudent>
                <CoverLetterGenerator />
              </RequireActiveStudent>
            }
          />

          <Route
            path="/student/cv-builder"
            element={
              <RequireActiveStudent>
                <StudentCVBuilder />
              </RequireActiveStudent>
            }
          />

          <Route
            path="/student/volunteer-circular"
            element={
              <RequireActiveStudent>
                <VolunteerCircularPage audience="student" />
              </RequireActiveStudent>
            }
          />

          <Route
            path="/student/grade-converter"
            element={
              <RequireActiveStudent>
                <GradeConverter />
              </RequireActiveStudent>
            }
          />

          <Route
            path="/student/cv-guide"
            element={
              <RequireActiveStudent>
                <CVBuilderGuide />
              </RequireActiveStudent>
            }
          />

          <Route
            path="/student/shadowing-task/:taskId"
            element={
              <RequireActiveStudent>
                <StudentShadowingTaskPage />
              </RequireActiveStudent>
            }
          />

          <Route
            path="/student/my-shadowing-tasks"
            element={
              <RequireActiveStudent>
                <StudentMyShadowingTasksPage />
              </RequireActiveStudent>
            }
          />
          <Route
            path="/student/speaking-practice"
            element={
              <RequireActiveStudent>
                <StudentAudioPage />
              </RequireActiveStudent>
            }
          />
        </Route>

        {/* ADMIN ROLE PROTECTED ROUTES */}
        <Route element={<RoleProtectedRoute allowedRoles={["ADMIN"]} />}>
          {/* Volunteer Circular: admin view inside the public layout; students open it from their dashboard once unlocked */}
          <Route element={<Feature />}>
            <Route path="/volunteer-circular" element={<VolunteerCircularPage audience="admin" />} />
          </Route>
          <Route path="/admin/volunteer-circular-access" element={<AdminVolunteerAccess />} />
          <Route path="/admin/visa-letter-check" element={<AdminVisaLetterCheck />} />
          <Route path="/admin/visa-waiting-cases" element={<AdminVisaWaitingCases />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/manage-students" element={<ManageStudents />} />
          <Route path="/admin/edit-student/:id" element={<EditStudent />} />
          <Route path="/admin/manage-courses" element={<ManageCourses />} />
          <Route path="/admin/create-course" element={<CreateCourse />} />
          <Route path="/admin/edit-course/:id" element={<EditCourse />} />
          <Route path="/admin/manage-batches" element={<ManageBatches />} />
          <Route path="/admin/course-plans" element={<AdminCoursePlans />} />
          <Route path="/admin/create-batch" element={<CreateBatch />} />
          <Route path="/admin/edit-batch/:id" element={<EditBatch />} />
          <Route
            path="/admin/manage-enrollments"
            element={<AdminEnrollments />}
          />
          <Route
            path="/admin/manage-appointments"
            element={<ManageAppointments />}
          />
          <Route
            path="/admin/translation-requests"
            element={<TranslationRequestsAdmin />}
          />
          <Route path="/admin/manage-mentors" element={<ManageAdminsMentors />} />
          <Route path="/admin/manage-quizzes" element={<AdminManageQuizzes />} />
          <Route path="/admin/create-quiz" element={<CreateQuizPage />} />
          <Route
            path="/quiz/:quizId/add-questions"
            element={<AddQuestionsPage />}
          />
          <Route
            path="/admin/manage-quizzes/:quizId/edit"
            element={<EditQuizPage />}
          />
          <Route
            path="/admin/manage-quizzes/:quizId/assign"
            element={<AssignQuizPage />}
          />
          <Route
            path="/admin/batch/:batchId/quiz-results"
            element={<AdminBatchQuizSummary />}
          />
          <Route
            path="/admin/payment-management"
            element={<AdminPaymentManagement />}
          />
          <Route
            path="/admin/due-installments"
            element={<DueInstallments />}
          />
          <Route
            path="/admin/financial-dashboard"
            element={<FinancialDashboard />}
          />
          <Route
            path="/admin/batch-progress-dashboard"
            element={<AdminProgressDashboard />}
          />
          <Route
            path="/admin/course-progress-management"
            element={<AdminStepManagement />}
          />
          <Route path="/admin/register-mentor" element={<MentorRegisterPage />} />
          {/* Legacy */}
          <Route path="/admin/manage-legacy" element={<AdminLegacyPending />} />
          <Route path="/admin/broadcast-email" element={<AdminBroadcastEmail />} />
          <Route
            path="/admin/writing-tasks"
            element={<AdminWritingTasksPage />}
          />
          <Route
            path="/admin/writing-tasks/create"
            element={<CreateWritingTaskPage />}
          />
          <Route
            path="/admin/writing-tasks/:taskId/edit"
            element={<EditWritingTaskPage />}
          />
          <Route
            path="/admin/writing-tasks/:taskId/assign"
            element={<AssignWritingTaskPage />}
          />
          <Route
            path="/admin/attendance-overview"
            element={<AdminAttendanceOverview />}
          />
          <Route
            path="/admin/regulation-review"
            element={<AdminRegulationReview />}
          />
          <Route
            path="/admin/regulation-templates"
            element={<CourseRegulationTemplates />}
          />
          <Route
            path="/admin/ausbildung-application-overview"
            element={<AdminAusbildungDashboard />}
          />
          <Route path="/admin/homework" element={<AdminHomeworkPage />} />
          <Route
            path="/admin/homework/create"
            element={<AdminCreateHomeworkPage />}
          />
          <Route
            path="/admin/homework/:homeworkId/edit"
            element={<AdminEditHomeworkPage />}
          />
          <Route
            path="/admin/homework/:homeworkId/assign"
            element={<AdminAssignHomeworkPage />}
          />
          <Route
            path="/admin/homework/:homeworkId/view"
            element={<AdminHomeworkViewPage />}
          />
          <Route
            path="/admin/vocabulary-management"
            element={<AdminVocabularyManagement />}
          />

          {/* EXAM ROUTES */}
          <Route
            path="/admin/exam-management"
            element={<AdminExamManagement />}
          />
          <Route
            path="/admin/exams/:examId/edit"
            element={<AdminEditExamSet />}
          />
          <Route path="/admin/exams/create" element={<AdminCreateExamSet />} />
          <Route
            path="/admin/exams/:examId/questions"
            element={<AdminManageQuestions />}
          />
          <Route
            path="/admin/exams/:examId/assign"
            element={<AdminAssignExam />}
          />
          <Route
            path="/admin/exams/assignments"
            element={<AdminExamAssignments />}
          />
          <Route
            path="/admin/exams/assignments/:assignmentId/grade"
            element={<AdminBatchGrading />}
          />
          <Route path="/admin/exams/results" element={<AdminExamResults />} />
          <Route path="/admin/class-test-management" element={<AdminClassTestManagement />} />
          <Route path="/admin/speaking-practice" element={<AdminAudioReviewPage />} />
          <Route
            path="/admin/exams/statistics"
            element={<AdminExamStatistics />}
          />
          <Route
            path="/admin/exams/:examId/builder"
            element={<AdminExamBuilder />}
          />
          <Route
            path="/admin/cover-letter-access"
            element={<CoverLetterAccessManagement />}
          />
          <Route
            path="/admin/cv-builder-access"
            element={<CvBuilderAccessManagement />}
          />

          {/* ✅ BLOG ADMIN ROUTES */}
          <Route path="/admin/manage-blogs" element={<AdminBlogManagement />} />
          <Route path="/admin/create-blog" element={<CreateEditBlog />} />
          <Route path="/admin/edit-blog/:id" element={<CreateEditBlog />} />
          <Route path="/admin/calendar" element={<AdminCalendarManagement />} />

          {/* SHADOWING TASK ROUTES */}
          <Route
            path="/admin/shadowing-tasks"
            element={<AdminShadowingTasksPage />}
          />
          <Route
            path="/admin/shadowing-tasks/create"
            element={<CreateShadowingTaskPage />}
          />
          <Route
            path="/admin/shadowing-tasks/:taskId/edit"
            element={<EditShadowingTaskPage />}
          />
          <Route
            path="/admin/shadowing-tasks/:taskId/assign"
            element={<AssignShadowingTaskPage />}
          />
          <Route
            path="/admin/shadowing-tasks/submissions/:submissionId/review"
            element={<ReviewShadowingSubmissionPage />}
          />
          <Route
            path="/admin/shadowing-tasks/:taskId/submissions"
            element={<AdminShadowingSubmissionsListPage />}
          />
          <Route path="/admin/monthly-visits" element={<MonthlyVisits />} />
          <Route path="/admin/activity-report" element={<AdminActivityReport />} />
          <Route path="/admin/mentor-tracker" element={<AdminMentorTracker />} />
          <Route path="/admin/employee-activity" element={<AdminEmployeeActivity />} />
          <Route path="/admin/speaking-partner-verifications" element={<AdminPracticeManagementPage initialTab="verification" />} />
          <Route path="/admin/speaking-partner-reports" element={<AdminPracticeManagementPage initialTab="reports" />} />
          <Route path="/admin/speaking-partner-call-usage" element={<AdminPracticeManagementPage initialTab="usage" />} />
          <Route path="/admin/speaking-partner-users" element={<AdminPracticeManagementPage initialTab="users" />} />

          {/* LECTURE ROUTES */}
          <Route
            path="/admin/course-lectures"
            element={<CourseLecturesLayout />}
          />
        </Route>

        {/* MENTOR ROLE PROTECTED ROUTES */}
        <Route element={<RoleProtectedRoute allowedRoles={["MENTOR"]} />}>
          <Route path="/mentor/dashboard" element={<MentorDashboard />} />
          <Route path="/mentor/my-batches" element={<MentorBatches />} />
          <Route
            path="/mentor/manage-quizzes/:quizId/assign"
            element={<AssignQuizPage />}
          />
          <Route path="/mentor/manage-quizzes" element={<MentorManageQuiz />} />
          <Route
            path="/mentor/batch-course-progress"
            element={<MentorBatchCourseProgress />}
          />
          <Route
            path="/mentor/progress-overview"
            element={<MentorProgressDashboard />}
          />
          <Route
            path="/mentor/writing-tasks"
            element={<MentorWritingTasksPage />}
          />
          <Route
            path="/mentor/writing-tasks/:taskId/assign"
            element={<MentorAssignWritingTaskPage />}
          />
          <Route
            path="/mentor/writing-task-results"
            element={<MentorBatchWritingTaskResultsPage />}
          />
          <Route path="/mentor/homework" element={<MentorHomeworkPage />} />
          <Route
            path="/mentor/homework/:homeworkId/view"
            element={<MentorHomeworkViewPage />}
          />
          <Route
            path="/mentor/homework/:homeworkId/assign"
            element={<MentorAssignHomeworkPage />}
          />
          <Route
            path="/mentor/exam-management"
            element={<MentorExamManagement />}
          />
          <Route
            path="/mentor/class-test-management"
            element={<MentorClassTestManagement />}
          />
          <Route
            path="/mentor/exams/:assignmentId/grade"
            element={<MentorGradeExam />}
          />
          <Route
            path="/mentor/exams/:assignmentId/results"
            element={<MentorExamResults />}
          />

          {/* ✅ ADD MENTOR SHADOWING ROUTES HERE */}
          <Route
            path="/mentor/shadowing-tasks"
            element={<MentorShadowingTasksPage />}
          />
          <Route
            path="/mentor/shadowing-tasks/:taskId/assign"
            element={<MentorAssignShadowingTaskPage />}
          />
          <Route
            path="/mentor/shadowing-tasks/batch-summary"
            element={<MentorBatchShadowingTaskSummary />}
          />
          <Route
            path="/mentor/shadowing-tasks/:taskId/submissions"
            element={<MentorReviewShadowingSubmissionPage />}
          />
          <Route
            path="/mentor/shadowing-tasks/submissions/:submissionId/review"
            element={<MentorReviewShadowingSubmissionDetailPage />}
          />
          <Route path="/mentor/speaking-practice" element={<MentorAudioReviewPage />} />
          <Route path="/mentor/task-checklist" element={<MentorTaskCheckinPage />} />
        </Route>

        {/* EMPLOYEE ROLE PROTECTED ROUTES */}
        <Route element={<RoleProtectedRoute allowedRoles={["EMPLOYEE"]} />}>
          <Route path="/employee/dashboard" element={<EmployeeDashboard />} />
          <Route path="/employee/daily-activity" element={<EmployeeDailyActivity />} />
        </Route>

        {/* Translation work — shared by employees and admins */}
        <Route element={<RoleProtectedRoute allowedRoles={["EMPLOYEE", "ADMIN"]} />}>
          <Route
            path="/employee/translation-requests"
            element={<TranslationQueue />}
          />
        </Route>

        {/* Fallback route - MUST be last */}
        <Route path="*" element={<Navigate to="/index" replace />} />
      </Routes>
    </Suspense>
  );
};

export default ALLRoutes;
